/* =====================================================================
   El enganche de la consola: los carriles se montan solos
   =====================================================================
   POR QUE EXISTE, CON EL CASO. `ui/modulos.jsx` tiene 388 lineas, 55
   `import` de pantallas y unas cincuenta entradas de menu. Es EL archivo
   caliente del cliente: todo el que añade una pantalla escribe su linea
   ahi. `Login.jsx` lo reescribio otra pestaña a las 02:52 del 30 de
   agosto de 2026 por exactamente la misma razon.

   Un carril ya no lo toca. Deja su archivo aqui:

       frontend/src/carriles/<carril>.modulos.jsx

   exportando por omision:

       export default {
         orden: 50,                       // donde va su grupo en el menu
         grupos: [{ grupo: 'Ventas', items: [
           { id: 'notas-cargo', etiqueta: 'Notas de cargo',
             icono: FileText, min: 'ENCARGADO', soloEmpresa: true,
             Comp: NotasDeCargo },
         ]}],
       };

   y `modulos.jsx` los agrega sin que nadie lo edite.

   OJO: AQUI NO HAY REACT-ROUTER. La directriz daba por hecho un
   `App.jsx` con rutas y un `.routes.jsx` por carril. No lo hay: la
   consola navega por modulos (`?m=<id>`, ver `lib/urlEstado.js`) y
   `App.jsx` solo decide entre invitado y sesion iniciada. El enganche
   cuelga del registro de modulos, que es el mecanismo de verdad.
   ===================================================================== */

/* =====================================================================
   POR QUE EL GLOB VA DENTRO DE UNA FUNCION Y CON UN `try`
   =====================================================================
   `import.meta.glob` es de Vite y no existe en Node — y las pruebas del
   cliente corren en Node (`node --test`, ver `pruebas/registrar.js`).
   Escrito a pelo, `modulos.jsx` revienta al cargarse y se llevo por
   delante trece archivos de prueba la primera vez que se intento.

   Y NO SIRVE PREGUNTAR `if (import.meta.glob)`: Vite sustituye la
   LLAMADA en tiempo de compilacion, no define la funcion, asi que la
   pregunta saldria falsa tambien en el navegador y el menu se quedaria
   sin los carriles sin que nadie viera un error.

   Dentro de una funcion sigue siendo una llamada, que es lo que Vite
   sustituye; y en Node la llamada revienta y se atrapa. Cada entorno
   hace lo suyo y ninguno finge.
   ===================================================================== */
function descubrir() {
  try {
    return import.meta.glob('./*.modulos.jsx', { eager: true }) ?? {};
  } catch {
    // Node: no hay glob de Vite. Las pruebas del cliente comprueban
    // `agregar()` directamente, que es donde vive toda la logica.
    return {};
  }
}

/* =====================================================================
   TODA LA LOGICA VIVE EN `agregar`, Y ES A PROPOSITO
   =====================================================================
   Ordenar y comprobar la forma es lo unico que puede fallar aqui, y es
   justo lo que el glob impide probar en Node. Separado, la prueba del
   cliente lo ejercita de verdad con entradas de mentira — en vez de
   comprobar una copia de la regla, que pasa aunque el codigo cambie.
   ===================================================================== */
export function agregar(modulos) {
  /* EL ORDEN NO DEPENDE DEL SISTEMA DE ARCHIVOS. Primero `orden`, y a
     igualdad, el nombre del archivo. Un menu que cambia de orden segun
     la maquina que compilo es un menu en el que la gente deja de
     encontrar las cosas donde las dejo. */
  const entradas = Object.entries(modulos)
    .map(([ruta, mod]) => ({ ruta, def: mod?.default ?? mod }))
    .sort((a, b) => {
      const oa = a.def?.orden ?? 100;
      const ob = b.def?.orden ?? 100;
      return oa !== ob ? oa - ob : a.ruta.localeCompare(b.ruta);
    });

  /* LO QUE ESTA MAL SE DICE, NO SE SALTA. Un carril que exporta algo con
     otra forma se quedaria sin menu y nadie lo notaria hasta que alguien
     buscara su pantalla — que es la peor forma de fallar. */
  const grupos = [];
  const vistos = new Map();
  for (const { ruta, def } of entradas) {
    if (!def || !Array.isArray(def.grupos)) {
      throw new Error(
        `El carril «${ruta}» no exporta { orden, grupos: [...] }.\n` +
        `  Qué pasó: su archivo existe pero no tiene la forma que espera el\n` +
        `  menú, así que sus pantallas no aparecerían y nadie lo notaría.\n` +
        `  Qué hacer: exporta por omisión un objeto con \`grupos\`, una lista\n` +
        `  de { grupo, items: [{ id, etiqueta, icono, min, Comp }] }.`,
      );
    }
    for (const g of def.grupos) {
      if (!g?.grupo || !Array.isArray(g.items)) {
        throw new Error(
          `El carril «${ruta}» trae un grupo sin \`grupo\` o sin \`items\`.\n` +
          `  Qué hacer: cada grupo es { grupo: 'Nombre', items: [...] }.`,
        );
      }
      for (const it of g.items) {
        if (!it?.id || !it?.etiqueta || !it?.Comp) {
          throw new Error(
            `El carril «${ruta}» trae un módulo sin \`id\`, \`etiqueta\` o ` +
            `\`Comp\`.\n  Qué pasó: el menú no puede pintarlo ni abrirlo.`,
          );
        }
        /* DOS MODULOS CON EL MISMO `id` ES UNO QUE NO SE ABRE NUNCA.
           `AppShell` busca por `id` y se queda con el primero, asi que
           el segundo existiria en el menu y llevaria a la pantalla del
           otro. Se dice aqui, no se descubre en un pasillo. */
        const antes = vistos.get(it.id);
        if (antes) {
          throw new Error(
            `El módulo «${it.id}» lo declaran «${antes}» y «${ruta}».\n` +
            `  Qué pasó: la consola busca por \`id\` y se queda con el primero,\n` +
            `  así que el segundo llevaría a la pantalla del primero.\n` +
            `  Qué hacer: que cada carril use su propio prefijo en los \`id\`.`,
          );
        }
        vistos.set(it.id, ruta);
      }
      grupos.push(g);
    }
  }
  return grupos;
}

/* =====================================================================
   DOS GRUPOS CON EL MISMO NOMBRE SON UNO · INT-3
   =====================================================================
   Autorizado por el dueño el 9 de septiembre de 2026. Sin esto, dos
   carriles que declaran «Operación» pintan DOS cabeceras «Operación» en
   el menú, una debajo de la otra, y quien busca una pantalla la busca en
   la primera y no está. Lo mismo si un carril declara un grupo que ya
   existe en `GRUPOS_BASE`.

   EL ORDEN ES EL DE LA PRIMERA APARICION, y eso es deliberado: un grupo
   que ya existe **no se mueve de sitio** porque alguien le añada una
   pantalla. Los items nuevos entran al final del grupo que ya estaba.

   MEDIDO EL DIA QUE SE ESCRIBIO: hoy no funde nada. `ventaja` declara
   «Compras y proveedores» y `escala` «Control», y ninguno de los dos
   choca con los cuatro de `GRUPOS_BASE` (Administración, Calidad,
   Operación, Vigilancia) ni entre sí. Así que esto no mueve ninguna
   pantalla hoy — entra para que el primer choque no se descubra en el
   menú de un cliente.
   ===================================================================== */
export function fundirGrupos(grupos) {
  const porNombre = new Map();
  const orden = [];
  for (const g of grupos) {
    const ya = porNombre.get(g.grupo);
    if (ya) {
      ya.items = [...ya.items, ...g.items];
      continue;
    }
    const copia = { ...g, items: [...g.items] };
    porNombre.set(g.grupo, copia);
    orden.push(copia);
  }
  return orden;
}

export function gruposDeCarriles() {
  return agregar(descubrir());
}

/* Para la prueba del cliente y para quien quiera mirar qué hay montado. */
export function idsDeCarriles() {
  return gruposDeCarriles().flatMap((g) => g.items.map((m) => m.id));
}
